"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { GridRowId } from "@mui/x-data-grid-pro";
import { calculateInitialEmailCadence } from "../helpers";
import { FetchCampaignsV2Response } from "@/api/interfaces/responses/campaigns";
import { User } from "@/api/users";
import { EmailCadence } from "@/interfaces/campaign";

interface AssignDialogContextType {
  assignDialogOpen: boolean;
  campaignIdsToAssign: GridRowId[];
  closeAssignDialog: () => void;
  emailCadence: EmailCadence;
  openAssignDialog: (campaignIds: GridRowId[]) => void;
  selectedAssignUsers: User[];
  setEmailCadence: (value: EmailCadence) => void;
  setSelectedAssignUsers: (users: User[]) => void;
}

const AssignDialogContext = createContext<AssignDialogContextType | undefined>(
  undefined,
);

export const useAssignDialog = () => {
  const context = useContext(AssignDialogContext);
  if (!context) {
    throw new Error("useAssignDialog must be used within AssignDialogProvider");
  }
  return context;
};

interface AssignDialogProviderProps {
  children: ReactNode;
  data: FetchCampaignsV2Response[];
}

export function AssignDialogProvider({
  children,
  data,
}: AssignDialogProviderProps) {
  const [campaignIdsToAssign, setCampaignIdsToAssign] = useState<GridRowId[]>(
    [],
  );
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAssignUsers, setSelectedAssignUsers] = useState<User[]>([]);
  const [emailCadence, setEmailCadence] = useState<EmailCadence>(
    EmailCadence.Unsubscribed,
  );

  const openAssignDialog = useCallback(
    (campaignIds: GridRowId[]) => {
      setCampaignIdsToAssign(campaignIds);
      setEmailCadence(
        calculateInitialEmailCadence(
          data.filter((campaign) => campaignIds.includes(campaign.id)),
        ),
      );
      setAssignDialogOpen(true);
    },
    [data],
  );

  const closeAssignDialog = useCallback(() => {
    setAssignDialogOpen(false);
  }, []);

  return (
    <AssignDialogContext.Provider
      value={{
        assignDialogOpen,
        closeAssignDialog,
        emailCadence,
        campaignIdsToAssign,
        openAssignDialog,
        selectedAssignUsers,
        setEmailCadence,
        setSelectedAssignUsers,
      }}
    >
      {children}
    </AssignDialogContext.Provider>
  );
}
